import React from 'react';
import { Alert, Content, Label, Radio, Title } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import type { GpuServerOfferProduct } from '../../../../types/models/GpuServer';
import { formatPrice, formatUnitPrice } from '../../../../utils/money';

interface ServerStepProps {
  products: GpuServerOfferProduct[];
  selectedProductId: number | null;
  onSelectProduct: (productId: number) => void;
  /** The system disk every server includes (from the offer), in GB. */
  diskGb: number;
  /** A start refusal that belongs to this step (`data.product_id`), shown verbatim. */
  error: string | null;
}

const availabilityText = (count: number): string => `Available in ${count} location${count === 1 ? '' : 's'}`;

const hasDescription = (description: string | null): description is string =>
  description !== null && description.trim() !== '';

/**
 * A product's public description, verbatim, as subtle secondary text — a French spec line that
 * states the included system disk (e.g. "4 vCPU, 16 Go de RAM, disque système de 100 Go"). Renders
 * nothing at all when the product has none. Shared with the review step.
 */
export const ProductDescription: React.FunctionComponent<{ description: string | null; id?: string }> = ({
  description,
  id,
}) =>
  hasDescription(description) ? (
    <div id={id} className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
      {description}
    </div>
  ) : null;

/**
 * Step 1 — choose the server. A sold-out product (no location with capacity right now) stays listed
 * but can't be selected. Each product shows its description under its label; the English footnote
 * restates the included system disk for everyone.
 */
const ServerStep: React.FunctionComponent<ServerStepProps> = ({
  products,
  selectedProductId,
  onSelectProduct,
  diskGb,
  error,
}) => (
  <>
    <Title headingLevel="h2" size="lg" className="pf-v6-u-mb-sm">
      Choose a server
    </Title>
    <Content component="p">Prices are per hour and stay fixed for the server&apos;s lifetime.</Content>
    {error !== null && (
      <Alert variant="danger" isInline title="This server cannot be started" className="pf-v6-u-mb-md">
        {error}
      </Alert>
    )}
    <Table aria-label="Servers on offer" variant="compact">
      <Thead>
        <Tr>
          <Th screenReaderText="Select a server" />
          <Th>Server</Th>
          <Th modifier="nowrap">Per hour incl. VAT</Th>
          <Th modifier="nowrap">Per hour excl. VAT</Th>
          <Th>Availability</Th>
        </Tr>
      </Thead>
      <Tbody>
        {products.map((product) => {
          const soldOut = product.available_locations.length === 0;
          return (
            <Tr key={product.id}>
              {/* A plain Radio in PF's check cell rather than `Td select`, whose radio is always named
                  "Select row N" — this one says which server it selects. */}
              <Td className="pf-v6-c-table__check">
                <Radio
                  id={`gpu-product-${product.id}`}
                  name="gpu-product"
                  aria-label={`Select ${product.label}`}
                  aria-describedby={
                    hasDescription(product.description) ? `gpu-product-${product.id}-description` : undefined
                  }
                  isChecked={selectedProductId === product.id}
                  isDisabled={soldOut}
                  onChange={() => onSelectProduct(product.id)}
                />
              </Td>
              <Td dataLabel="Server">
                {product.label}
                <ProductDescription description={product.description} id={`gpu-product-${product.id}-description`} />
              </Td>
              <Td dataLabel="Per hour incl. VAT" modifier="nowrap">
                {formatPrice(product.first_hour_ttc)}
              </Td>
              <Td dataLabel="Per hour excl. VAT" modifier="nowrap">
                {formatUnitPrice(product.hourly_price_ht)}
              </Td>
              <Td dataLabel="Availability">
                {soldOut ? (
                  <Label color="grey" isCompact>
                    Sold out
                  </Label>
                ) : (
                  availabilityText(product.available_locations.length)
                )}
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
    <Content component="small" className="pf-v6-u-display-block pf-v6-u-mt-sm pf-v6-u-text-color-subtle">
      Every server includes a {diskGb} GB system disk.
    </Content>
  </>
);

export default ServerStep;
